import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { ArrowLeft, Check } from "lucide-react";
import { QUESTIONS, DEFAULT_ANSWERS } from "@/lib/quizConfig";
import { getRecommendations } from "@/lib/api";
import { GripGuide } from "@/components/GripGuide";
import { toast } from "sonner";

const IconById = ({ name, size = 18 }) => {
  const C = LucideIcons[name] || LucideIcons.Circle;
  return <C size={size} strokeWidth={1.75} />;
};

export default function Quiz({ setResults, setAnswers }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState({ ...DEFAULT_ANSWERS });
  const [notSureCount, setNotSureCount] = useState(0);
  const [gripGuideOpen, setGripGuideOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const q = QUESTIONS[step];
  const total = QUESTIONS.length;
  const progress = useMemo(() => ((step + 1) / total) * 100, [step, total]);

  const submit = async (finalAnswers) => {
    setLoading(true);
    try {
      const data = await getRecommendations(finalAnswers);
      setAnswers(finalAnswers);
      setResults(data.recommendations || []);
      navigate("/results");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't fetch paddles. Please try again.");
      setLoading(false);
    }
  };

  const handleSelect = (opt) => {
    if (opt.opensGuide) {
      setGripGuideOpen(true);
      return;
    }
    const next = { ...selected, [q.id]: opt.id };
    setSelected(next);

    let ns = notSureCount;
    if (opt.id === "not_sure") ns += 1;
    setNotSureCount(ns);

    if (step >= total - 1) {
      submit(next);
    } else {
      // If user hit "Not sure" twice, short-circuit to safe defaults
      if (ns >= 2) {
        toast("Showing our top picks for most players", { icon: "✨" });
      }
      setTimeout(() => setStep(step + 1), 180);
    }
  };

  const handleBack = () => {
    if (step === 0) {
      navigate("/");
      return;
    }
    setStep(step - 1);
  };

  const handleSkip = () => {
    const next = { ...selected }; // keep default for this q
    setSelected(next);
    if (step >= total - 1) submit(next);
    else setStep(step + 1);
  };

  return (
    <div className="cd-container cd-court-bg" data-testid="quiz-page">
      <div className="cd-progress-wrap cd-fade-in" key={`p-${step}`}>
        <div className="cd-progress-track" role="progressbar" aria-valuenow={progress}>
          <div className="cd-progress-fill-el" style={{ width: `${progress}%` }} />
        </div>
        <div className="cd-progress-meta">
          <span className="cd-micro" data-testid="quiz-progress">
            Question {step + 1} of {total}
          </span>
          <button className="cd-btn-ghost" onClick={handleBack} data-testid="quiz-back-btn">
            <ArrowLeft size={14} style={{ marginRight: 6 }} /> Back
          </button>
        </div>
      </div>

      <div className="cd-slide-in" key={`q-${step}`} style={{ paddingTop: 12 }}>
        <h1 className="cd-h1" style={{ marginBottom: 8 }} data-testid="quiz-title">
          {q.title}
        </h1>
        <p className="cd-sub" style={{ marginBottom: 22 }}>{q.hint}</p>

        <div style={{ display: "grid", gap: 12 }}>
          {q.options.map((opt) => {
            const isSel = selected[q.id] === opt.id;
            return (
              <button
                key={opt.id}
                className={`cd-option ${isSel ? "selected" : ""}`}
                onClick={() => handleSelect(opt)}
                disabled={loading}
                data-testid={`quiz-option-${q.id}-${opt.id}`}
                aria-pressed={isSel}
              >
                <div className="cd-icon-wrap">
                  <IconById name={opt.icon} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{opt.label}</div>
                  {opt.sub && <div className="cd-micro" style={{ marginTop: 2 }}>{opt.sub}</div>}
                </div>
                {isSel && <Check size={18} color="var(--cd-primary)" />}
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 22 }}>
          <button className="cd-btn-ghost" onClick={handleSkip} data-testid="quiz-skip-btn">
            Skip this
          </button>
          {loading && <span className="cd-micro">Finding your matches…</span>}
        </div>
      </div>

      <GripGuide open={gripGuideOpen} onOpenChange={setGripGuideOpen} />
    </div>
  );
}
